import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  List,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCalendar,
  IconCheck,
  IconCreditCard,
  IconReceipt2,
  IconRefresh,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

const formatMinor = (minor: number, currency: string) => {
  if (minor === 0) return 'Free';
  const amount = (minor / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency} ${amount}`;
};

const daysUntil = (date: string | Date): number =>
  Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000));

function SubscriptionPage() {
  const utils = trpc.useUtils();
  const me = trpc.billing.subscriptions.mine.useQuery();
  const plans = trpc.billing.plans.list.useQuery({ activeOnly: true });
  const invoices = trpc.billing.subscriptions.invoicesMine.useQuery();
  const myManual = trpc.billing.manual.myLatest.useQuery();

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [cancelOpen, cancelH] = useDisclosure(false);
  const [gateway, setGateway] = useState<'stripe' | 'paypal' | 'razorpay' | 'paystack' | 'manual'>(
    'stripe',
  );
  const [manualReference, setManualReference] = useState('');

  const checkout = trpc.billing.checkout.create.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else if (data.manualOnly) {
        setGateway('manual');
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const submitManual = trpc.billing.manual.submit.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Manual subscription submitted for review', color: 'green' });
      close();
      void utils.billing.manual.myLatest.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const cancel = trpc.billing.subscriptions.cancel.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Subscription will cancel at the end of the current period.',
        color: 'orange',
      });
      cancelH.close();
      void utils.billing.subscriptions.mine.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const reactivate = trpc.billing.subscriptions.reactivate.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Cancellation undone — subscription will renew normally.',
        color: 'green',
      });
      void utils.billing.subscriptions.mine.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const sub = me.data;
  // Explicit shape — breaks tsc's deep recursion (TS2589) on the
  // SimpleGrid + tRPC inferred type intersection.
  interface PlanRow {
    id: number;
    name: string;
    priceMinor: number;
    currency: string;
    interval: string;
    trialDays: number;
    features: unknown;
  }
  const planList = (plans.data ?? []) as readonly PlanRow[];
  const planById = (id: number) => planList.find((p) => p.id === id);
  const currentPlanId = sub?.plan?.id ?? null;
  const isActiveOrTrialing = sub && ['active', 'trialing'].includes(sub.status);
  const periodEnd = sub ? new Date(sub.currentPeriodEnd) : null;
  const daysLeft = periodEnd ? daysUntil(periodEnd) : null;

  const onSubscribe = (planId: number) => {
    setSelectedPlanId(planId);
    setGateway('stripe');
    setManualReference('');
    open();
  };

  const proceed = () => {
    if (!selectedPlanId) return;
    if (gateway === 'manual') {
      submitManual.mutate({ planId: selectedPlanId, manualReference });
    } else {
      checkout.mutate({
        planId: selectedPlanId,
        gateway,
        successUrl: `${window.location.origin}/vendor/payment-success`,
        cancelUrl: `${window.location.origin}/vendor/subscription`,
      });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Title order={2}>Subscription</Title>

        {/* ── Current plan card ─────────────────────────────── */}
        {me.isPending ? (
          <Skeleton height={110} />
        ) : sub ? (
          <Card withBorder>
            <Group justify="space-between" wrap="wrap" gap="md">
              <Stack gap={4}>
                <Text size="sm" c="dimmed">
                  Current plan
                </Text>
                <Group gap="xs" align="center">
                  <Title order={3}>{sub.plan?.name ?? '—'}</Title>
                  <Badge
                    color={
                      sub.status === 'active'
                        ? 'green'
                        : sub.status === 'trialing'
                          ? 'blue'
                          : sub.status === 'past_due'
                            ? 'yellow'
                            : 'gray'
                    }
                    variant="light"
                  >
                    {sub.status}
                  </Badge>
                  {sub.cancelAtPeriodEnd && (
                    <Badge color="orange" variant="light">
                      cancels at period end
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <IconCalendar size={14} />
                  <Text size="xs" c="dimmed">
                    {sub.cancelAtPeriodEnd
                      ? `Ends on ${periodEnd?.toLocaleDateString()}`
                      : sub.status === 'trialing'
                        ? `Trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} (${periodEnd?.toLocaleDateString()})`
                        : `Renews on ${periodEnd?.toLocaleDateString()}`}
                    {sub.plan?.priceMinor
                      ? ` · ${formatMinor(sub.plan.priceMinor, sub.plan.currency ?? 'INR')} / ${sub.plan.interval}`
                      : ''}
                  </Text>
                </Group>
                {sub.status === 'past_due' && (
                  <Alert color="yellow" icon={<IconAlertCircle size={14} />} py={4}>
                    Payment is overdue — renew to keep access.
                  </Alert>
                )}
                {sub.status === 'trialing' && daysLeft !== null && daysLeft <= 3 && (
                  <Alert color="orange" icon={<IconAlertCircle size={14} />} py={4}>
                    Trial expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''} — subscribe to
                    continue.
                  </Alert>
                )}
              </Stack>

              <Group gap="xs">
                {isActiveOrTrialing && !sub.cancelAtPeriodEnd && (
                  <Button
                    variant="subtle"
                    color="red"
                    size="sm"
                    leftSection={<IconX size={14} />}
                    onClick={cancelH.open}
                  >
                    Cancel subscription
                  </Button>
                )}
                {sub.cancelAtPeriodEnd && (
                  <Tooltip label="Undo cancellation and keep your subscription active">
                    <Button
                      variant="light"
                      color="green"
                      size="sm"
                      leftSection={<IconRefresh size={14} />}
                      loading={reactivate.isPending}
                      onClick={() => reactivate.mutate()}
                    >
                      Keep subscription
                    </Button>
                  </Tooltip>
                )}
              </Group>
            </Group>
          </Card>
        ) : (
          <Alert color="yellow" icon={<IconCreditCard />}>
            You don't have an active subscription. Pick a plan below to get started.
          </Alert>
        )}

        {/* Manual subscription status alerts */}
        {myManual.data?.status === 'pending' && (
          <Alert color="blue" icon={<IconUpload />}>
            Manual subscription request for{' '}
            <strong>{planById(myManual.data.planId)?.name ?? 'a plan'}</strong> is pending admin
            review. We'll notify you once it's processed.
          </Alert>
        )}
        {myManual.data?.status === 'rejected' && (
          <Alert color="red">
            Your last manual request was rejected:{' '}
            {myManual.data.rejectionReason ?? 'no reason given'}. You may submit a new one.
          </Alert>
        )}

        {/* ── Plans grid ─────────────────────────────────────── */}
        <Divider label="Available plans" labelPosition="left" />
        {plans.isPending ? (
          <Skeleton height={300} />
        ) : (
          <Group align="stretch" wrap="wrap" gap="md">
            {planList.map((p) => {
              const isCurrent = currentPlanId === p.id;
              const isUpgrade = !isCurrent && sub && p.priceMinor > (sub.plan?.priceMinor ?? 0);
              const isDowngrade = !isCurrent && sub && p.priceMinor < (sub.plan?.priceMinor ?? 0);
              return (
                <Paper
                  key={p.id}
                  withBorder
                  shadow={isCurrent ? 'md' : undefined}
                  p="md"
                  style={{
                    width: 240,
                    display: 'flex',
                    flexDirection: 'column',
                    borderColor: isCurrent ? 'var(--mantine-color-blue-5)' : undefined,
                  }}
                >
                  <Group justify="space-between" mb={4}>
                    <Text fw={700} size="lg">
                      {p.name}
                    </Text>
                    {isCurrent && (
                      <Badge color="blue" variant="light">
                        Current
                      </Badge>
                    )}
                    {isUpgrade && (
                      <Badge color="green" variant="light">
                        Upgrade
                      </Badge>
                    )}
                    {isDowngrade && (
                      <Badge color="gray" variant="light">
                        Downgrade
                      </Badge>
                    )}
                  </Group>
                  <Title order={2} mt={4}>
                    {formatMinor(p.priceMinor, p.currency)}
                  </Title>
                  <Text size="xs" c="dimmed" mb="sm">
                    per {p.interval}
                    {p.trialDays > 0 && ` · ${p.trialDays}-day free trial`}
                  </Text>
                  <List
                    spacing="xs"
                    size="sm"
                    icon={<IconCheck size={14} color="var(--mantine-color-green-6)" />}
                    style={{ flex: 1 }}
                  >
                    {((p.features as string[]) ?? []).map((f) => (
                      <List.Item key={f}>{f}</List.Item>
                    ))}
                  </List>
                  <Button
                    mt="md"
                    fullWidth
                    disabled={isCurrent}
                    variant={isCurrent ? 'light' : isUpgrade ? 'filled' : 'default'}
                    color={isUpgrade ? 'green' : isDowngrade ? 'gray' : undefined}
                    onClick={() => !isCurrent && onSubscribe(p.id)}
                  >
                    {isCurrent
                      ? 'Current plan'
                      : isUpgrade
                        ? 'Upgrade'
                        : isDowngrade
                          ? 'Downgrade'
                          : 'Subscribe'}
                  </Button>
                </Paper>
              );
            })}
            {(plans.data ?? []).length === 0 && (
              <Text c="dimmed">No plans available. Contact admin.</Text>
            )}
          </Group>
        )}

        {/* ── Invoices ────────────────────────────────────────── */}
        <Divider label="Invoices" labelPosition="left" />
        <Card p={0}>
          <ScrollArea>
            {invoices.isPending ? (
              <Skeleton height={120} m="md" />
            ) : (invoices.data ?? []).length === 0 ? (
              <Text c="dimmed" ta="center" p="md">
                No invoices yet.
              </Text>
            ) : (
              <Stack gap={0}>
                {(invoices.data ?? []).map((inv) => (
                  <Group key={inv.id} justify="space-between" p="sm">
                    <Group gap="sm">
                      <IconReceipt2 size={18} stroke={1.4} />
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {formatMinor(inv.amountMinor, inv.currency)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(inv.periodStart).toLocaleDateString()} –{' '}
                          {new Date(inv.periodEnd).toLocaleDateString()}
                        </Text>
                      </Stack>
                    </Group>
                    <Group gap="xs">
                      <Badge
                        color={
                          inv.status === 'paid'
                            ? 'green'
                            : inv.status === 'open'
                              ? 'yellow'
                              : 'gray'
                        }
                        variant="light"
                      >
                        {inv.status}
                      </Badge>
                      {inv.hostedUrl && (
                        <Button
                          size="xs"
                          variant="subtle"
                          component="a"
                          href={inv.hostedUrl}
                          target="_blank"
                        >
                          View
                        </Button>
                      )}
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </ScrollArea>
        </Card>
      </Stack>

      {/* Subscribe modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={`Subscribe to ${planById(selectedPlanId ?? 0)?.name ?? 'plan'}`}
        centered
      >
        <Stack>
          <SegmentedControl
            value={gateway}
            onChange={(v) => setGateway(v as typeof gateway)}
            data={[
              { value: 'stripe', label: 'Stripe' },
              { value: 'paypal', label: 'PayPal' },
              { value: 'razorpay', label: 'Razorpay' },
              { value: 'paystack', label: 'Paystack' },
              { value: 'manual', label: 'Bank transfer' },
            ]}
          />
          {gateway === 'manual' ? (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Transfer the plan amount to our bank account. Details are available in{' '}
                <Link to="/vendor/settings">Settings → Billing</Link>. Then enter your transaction
                reference below — admin will activate your plan once confirmed.
              </Text>
              <TextInput
                label="Transaction / UTR reference"
                placeholder="UTR / IMPS / NEFT / SWIFT reference"
                value={manualReference}
                onChange={(e) => setManualReference(e.currentTarget.value)}
              />
            </Stack>
          ) : (
            <Alert color="blue" variant="light">
              You'll be redirected to {gateway} to complete payment securely. Gateway integrations
              are being configured — try <strong>Bank transfer</strong> to test the manual approval
              flow.
            </Alert>
          )}
          <Group justify="end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={checkout.isPending || submitManual.isPending}
              onClick={proceed}
              disabled={gateway === 'manual' && !manualReference.trim()}
            >
              {gateway === 'manual' ? 'Submit for review' : 'Continue to payment'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Cancel confirmation modal */}
      <Modal opened={cancelOpen} onClose={cancelH.close} title="Cancel subscription" centered>
        <Stack>
          <Alert color="orange" icon={<IconAlertCircle />}>
            Your subscription will remain active until{' '}
            <strong>{periodEnd?.toLocaleDateString()}</strong> and then expire. You can undo this
            any time before that date.
          </Alert>
          <Group justify="end">
            <Button variant="default" onClick={cancelH.close}>
              Keep subscription
            </Button>
            <Button color="red" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Yes, cancel at period end
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/subscription')({ component: SubscriptionPage });
